import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Clock, Ticket, ArrowRight, Mic, Radio, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
// Backend-driven (Phase 10F): `events` are adapted published events from the parent.
export const FeaturedShowBanner = ({ events = [] }) => {
  // The featured show is the published event flagged featured (e.g. Orange Street).
  const show = events.find((e) => e.featured);
  if (!show) return null;

  return (
    <section className="py-16 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-r from-orange-950/50 via-gray-950 to-amber-950/40" />
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-72 h-72 bg-orange-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-500/20">
              <Mic className="w-6 h-6 text-orange-400" />
            </div>
            <div>
              <h2 className="text-2xl font-display font-bold text-white">Featured Show</h2>
              <p className="text-sm text-gray-400">Live & loud — the show everyone's talking about</p>
            </div>
          </div>
          <Link to="/events">
            <Button variant="ghost" className="text-orange-400 hover:text-orange-300 hover:bg-orange-400/10">
              View All Events
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>

        {/* Show Card */}
        <Card className="overflow-hidden border-orange-500/30 bg-gray-900/80 backdrop-blur-sm">
          <div className="flex flex-col lg:flex-row">
            {/* Image Section */}
            <div className="lg:w-2/5 relative">
              <Link to={`/event/${show.slug}`}>
                <img
                  src={show.image}
                  alt={show.title}
                  className="w-full h-64 lg:h-full object-cover transition-transform duration-500 hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-gray-900/50 lg:bg-gradient-to-l" />

                {/* Badges */}
                <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                  <Badge className="bg-gradient-to-r from-orange-500 to-amber-500 text-white border-none shadow-lg">
                    <Radio className="w-3 h-3 mr-1" />
                    Live Show
                  </Badge>
                  <Badge className="bg-orange-500/90 text-white border-none">
                    Soundwolves Presents
                  </Badge>
                </div>
              </Link>
            </div>

            {/* Content Section */}
            <CardContent className="lg:w-3/5 p-6 lg:p-8 space-y-5">
              <div>
                <div className="flex flex-wrap gap-2 mb-3">
                  <Badge variant="outline" className="bg-orange-500/10 border-orange-500/30 text-orange-400">
                    <Calendar className="w-3 h-3 mr-1" />
                    {show.date}
                  </Badge>
                  <Badge variant="outline" className="bg-amber-500/10 border-amber-500/30 text-amber-400">
                    <MapPin className="w-3 h-3 mr-1" />
                    {show.venue} • {show.city}
                  </Badge>
                  <Badge variant="outline" className="bg-orange-500/10 border-orange-500/30 text-orange-400">
                    <Clock className="w-3 h-3 mr-1" />
                    {show.time}{show.endTime ? ` – ${show.endTime}` : ''}
                  </Badge>
                </div>

                <Link to={`/event/${show.slug}`}>
                  <h3 className="text-2xl lg:text-3xl font-display font-bold text-white hover:text-orange-400 transition-colors mb-2">
                    {show.title}
                  </h3>
                </Link>
                <p className="text-gray-400">{show.artist}</p>
              </div>

              {/* What to Expect */}
              {(show.whatToExpect?.length || show.highlights?.length) ? (
                <div>
                  <p className="text-xs text-gray-500 mb-2">What to expect:</p>
                  {show.whatToExpect?.length ? (
                    <ul className="space-y-1.5">
                      {show.whatToExpect.map((item, i) => (
                        <li key={i} className="text-sm text-gray-300 leading-relaxed">
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {show.highlights.slice(0, 4).map((highlight, i) => (
                        <Badge key={i} variant="outline" className="bg-gray-800/50 border-gray-700 text-gray-400 text-xs">
                          {highlight}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ) : null}

              {/* Price & CTA */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-gray-800">
                <div>
                  <p className="text-xs text-gray-500">Tickets from</p>
                  <span className="text-2xl font-bold text-orange-400">{show.price}</span>
                </div>
                <div className="flex flex-col items-start sm:items-end gap-1">
                  <a href={show.ticketLink} target="_blank" rel="noopener noreferrer" aria-label="Get tickets on Eventbrite (opens in a new tab)">
                    <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-lg shadow-orange-500/25">
                      <Ticket className="w-4 h-4 mr-2" />
                      Get Tickets
                      <ExternalLink className="w-3.5 h-3.5 ml-2" />
                    </Button>
                  </a>
                  <span className="text-[11px] text-gray-500">Opens Eventbrite • from {show.price}</span>
                </div>
              </div>
            </CardContent>
          </div>
        </Card>
      </div>
    </section>
  );
};

export default FeaturedShowBanner;
